import { useEditor, Editor, BubbleMenu, FloatingMenu } from '@tiptap/react'
import { forwardRef, useRef, useImperativeHandle, useState } from 'react'
import { useDecision } from '@optimizely/react-sdk'
import { useMention, useMentionUtils } from 'src/components/commentInput/hooks'
import { TiptapEditorProps, TiptapMethods } from './tiptap.types'
import { tippyOptions } from './tiptap.constants'
import { MenuBarEditor } from './menuBarEditor'
import { CustomEditorContent, TipTapContainer } from './tiptap.styles'
import { getUrlPreviewLink } from './utils/getUrlPreviewLink'
import { Counter } from './counter'
import { getTextAreaHeight } from './utils/getTextAreaHeight'
import LinkEditor from './linkEditor/linkEditor'
import { LinkEditorModal } from './linkEditorModal'
import useLinkMenu from './hooks/useLinkMenu/useLinkMenu'

const TiptapEditor = forwardRef(
  (
    {
      content,
      onChange,
      postType,
      className,
      extensions,
      placeholder,
      isOpenUpload,
      isFullScreen,
      isFileUploaded,
      onMentionSuccess,
      onMentionError,
      onMentionClick,
      isEdit = false
    }: TiptapEditorProps,
    ref: React.ForwardedRef<TiptapMethods>
  ) => {
    const [{ enabled: enableLinkEditor }] = useDecision('com_adm_web_enable_link_editor_tiptap')
    const [postMentionFeatureFlag] = useDecision('com_eng_web_enable_post_mention')
    const [showModalLinkEditor, setShowModalLinkEditor] = useState<boolean>(false)
    const [url, setUrl] = useState<string>('')
    const [link, setLink] = useState<string>('')
    const editorRef: React.MutableRefObject<Editor | null> = useRef(null)
    const { extractMentionsFromHTML, parseMentions } = useMentionUtils()
    const { mentionsEditorExtensions } = useMention({
      enable: postMentionFeatureFlag.enabled,
      mentionConfig: {
        appendTo: /* istanbul ignore next */ () => document.querySelector('body'),
        zIndex: 1041
      },
      onMentionSuccess,
      onMentionError,
      onMentionClick
    })
    const editor = useEditor({
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      extensions: [...extensions, ...mentionsEditorExtensions],
      content: content,
      onCreate: /* istanbul ignore next */ ({ editor }) => {
        content && editor.commands.setContent(content)
      },
      onUpdate: /* istanbul ignore next */ ({ editor }) => {
        const mentions = extractMentionsFromHTML(editor.getHTML())
        const description = parseMentions({ text: editor.getHTML(), mentions })
        const descriptionOnlyText = parseMentions({ text: editor.getText(), mentions })
        const newLink = getUrlPreviewLink({
          description: editor.getText(),
          link
        })

        setLink(newLink as string)
        onChange({
          description,
          descriptionOnlyText,
          linkUrl: newLink || ''
        })
      }
    })

    useImperativeHandle(ref, () => ({
      editor: editorRef.current
    }))

    const { handleOpenModal, handleCloseModal, saveLink, removeLink } = useLinkMenu({
      setShowModalLinkEditor,
      setUrl,
      url,
      editor: editorRef.current
    })

    if (!editor) return null

    editorRef.current = editor

    const textAreaHeight = getTextAreaHeight({
      isFullScreen,
      isEdit,
      isFileUploaded,
      postType,
      isOpenUpload
    })

    return (
      <TipTapContainer className={className} $textAreaHeight={textAreaHeight}>
        <>
          <div className="_w-full">
            <FloatingMenu
              className="menu-bar-tiptap _w-full"
              editor={editorRef.current}
              tippyOptions={{ ...tippyOptions, placement: 'bottom-start' }}
            >
              <MenuBarEditor editor={editorRef.current} />
            </FloatingMenu>
          </div>

          <div>
            <BubbleMenu
              shouldShow={({ from, to }) => {
                return from !== to
              }}
              className="bubble-bar-menu"
              editor={editorRef.current}
              tippyOptions={{ ...tippyOptions, placement: 'bottom' }}
            >
              <MenuBarEditor
                editor={editorRef.current}
                setShowModalLinkEditor={handleOpenModal}
                shouldShowLinkEditor
              />
            </BubbleMenu>
          </div>

          {enableLinkEditor && (
            <div>
              <BubbleMenu
                className="bubble-bar-menu"
                tippyOptions={{ duration: 150 }}
                editor={editor}
                shouldShow={({ editor, from, to }) => {
                  return from === to && editor.isActive('link')
                }}
              >
                <LinkEditor onOpenModal={handleOpenModal} />
              </BubbleMenu>
            </div>
          )}

          <div>
            <LinkEditorModal
              url={url}
              show={showModalLinkEditor}
              onChangeUrl={(e) => setUrl(e.target.value)}
              onClose={handleCloseModal}
              onRemoveLink={removeLink}
              onSaveLink={saveLink}
            />
          </div>

          <div>
            <CustomEditorContent
              $isFullScreen={isFullScreen}
              $placeholder={placeholder}
              editor={editorRef.current}
            />

            <Counter
              characterCount={editor.storage.characterCount.characters()}
              isFullScreen={isFullScreen}
            />
          </div>
        </>
      </TipTapContainer>
    )
  }
)

export default TiptapEditor
